import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { FaMapMarkerAlt, FaExpand, FaCompress } from 'react-icons/fa';

const TraderLocationMap = ({ 
  latitude, 
  longitude, 
  address, 
  businessName, 
  accuracy,
  height = 200,
  showFullscreen = true 
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);

  // AWS API key for MapLibre
  const API_KEY = "v1.public.eyJqdGkiOiI4MjM1YTE4NS00ZjBkLTQzNWItOTBjYy0wZDlmNGZhNjE0ZTcifTAFLwwypCRs1_BqhMeu4hkGTxcy3H2Pqp_TZbbplWeojkUGqHFfXzpQrqbmgTzfT-N7cRYEhBVNrw87qZkp3jS1MoyU5T1OlptmoI3ZJDK9dZt3cS1ixmzx_0_8HuorvlEezzpSURu_1HjUM_9DGr8LfxXPgIZCHsyuKHOZbDkCfqTk2_g9JAiYEnrfIMgQ8DaXAUjE1Vb_pK29eyzzzNIuDsqbCgeO9ui6Gj4XtJLrUQ8vsIagG_qwA4JBrGV_uhx6_7zrk1cbLC7o13pueIvgyURNxCksP8m3-efbINX4B-J187w3FC9lFQeEHDXm8skDsifxdFvGLyhZqYNA_HQ.ZWU0ZWIzMTktMWRhNi00Mzg0LTllMzYtNzlmMDU3MjRmYTkx";

  useEffect(() => {
    if (!latitude || !longitude || !mapContainer.current) return;

    try {
      // Initialize map
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: `https://api.maptiler.com/maps/streets/style.json?key=${API_KEY}`,
        center: [parseFloat(longitude), parseFloat(latitude)],
        zoom: 15,
        attributionControl: false
      });

      // Add navigation controls
      map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

      // Add marker when map loads
      map.current.on('load', () => {
        setMapLoaded(true);

        // Add marker
        new maplibregl.Marker({
          color: '#e74c3c',
          scale: 1.2
        })
          .setLngLat([parseFloat(longitude), parseFloat(latitude)])
          .addTo(map.current);

        // Add popup with business info
        new maplibregl.Popup({
          offset: 25,
          closeButton: false,
          closeOnClick: false
        })
          .setLngLat([parseFloat(longitude), parseFloat(latitude)])
          .setHTML(`
            <div style="padding: 8px; max-width: 200px;">
              <h4 style="margin: 0 0 4px 0; font-size: 14px; color: #333;">
                ${businessName || 'Trader Location'}
              </h4>
              ${address ? `<p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">${address}</p>` : ''}
              <p style="margin: 0; font-size: 11px; color: #999;">
                📍 ${parseFloat(latitude).toFixed(6)}, ${parseFloat(longitude).toFixed(6)}
                ${accuracy ? `<br/>±${Math.round(accuracy)}m accuracy` : ''}
              </p>
            </div>
          `)
          .addTo(map.current);

        // Add accuracy circle if available
        if (accuracy && accuracy > 0) {
          map.current.addSource('accuracy-circle', {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(longitude), parseFloat(latitude)]
              }
            }
          });

          map.current.addLayer({
            id: 'accuracy-circle',
            type: 'circle',
            source: 'accuracy-circle',
            paint: {
              'circle-radius': {
                stops: [
                  [0, 0],
                  [20, accuracy * 0.5] // Approximate pixel radius based on accuracy
                ],
                base: 2
              },
              'circle-color': '#3498db',
              'circle-opacity': 0.2,
              'circle-stroke-color': '#3498db',
              'circle-stroke-width': 1,
              'circle-stroke-opacity': 0.5
            }
          });
        }
      });

      map.current.on('error', (e) => {
        console.error('Map error:', e);
        setError('Failed to load map');
      });

    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to initialize map');
    }

    // Cleanup
    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, [latitude, longitude, address, businessName, accuracy]);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    // Resize map after fullscreen toggle
    setTimeout(() => {
      if (map.current) {
        map.current.resize();
      }
    }, 100);
  };

  // Don't render if no coordinates
  if (!latitude || !longitude) {
    return (
      <div style={{
        height: height,
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6c757d',
        fontSize: '14px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <FaMapMarkerAlt size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
          <div>No location data available</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        height: height,
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#856404',
        fontSize: '14px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <FaMapMarkerAlt size={24} style={{ marginBottom: '8px' }} />
          <div>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{
        position: 'relative',
        height: height,
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #e9ecef'
      }}>
        <div 
          ref={mapContainer} 
          style={{ 
            width: '100%', 
            height: '100%' 
          }} 
        />
        
        {showFullscreen && (
          <button
            onClick={toggleFullscreen}
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '6px',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 1000
            }}
            title="Toggle fullscreen"
          >
            <FaExpand size={12} />
          </button>
        )}

        {!mapLoaded && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(248, 249, 250, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6c757d',
            fontSize: '14px'
          }}>
            Loading map...
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            position: 'relative',
            width: '90vw',
            height: '80vh',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div 
              ref={mapContainer} 
              style={{ 
                width: '100%', 
                height: '100%' 
              }} 
            />
            
            <button
              onClick={toggleFullscreen}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 1000
              }}
              title="Exit fullscreen"
            >
              <FaCompress size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TraderLocationMap;
